import { shortName } from '@/components/value-chain/nodeVisuals';
import type { TreeIndex } from '@/components/value-chain/treeIndex';
import { ancestorsOf, childrenOf, companiesOf } from '@/components/value-chain/treeIndex';
import type { ValueChainGraph, ValueChainNode } from '@/types/valueChain';

export type RevenueEvidence = 'confirmed' | 'inferred' | 'unknown' | 'mixed';

export type RevenueSlice = {
  companyId: string;
  companyName: string;
  categoryId: string;
  categoryLabel: string;
  country: string;
  revenue: number;
  rank: number;
  evidence: string;
  partId: string;
  partName: string;
};

export type RevenueCompany = {
  id: string;
  name: string;
  country: string;
  revenue: number;
  sizeValue: number;
  mentions: number;
  growthPct: number | null;
  evidence: RevenueEvidence;
  partNames: string[];
  node: ValueChainNode | null;
};

export type RevenueCategory = {
  id: string;
  label: string;
  revenue: number;
  companies: RevenueCompany[];
};

export type RevenueMapModel = {
  totalRevenue: number;
  mappedRevenue: number;
  unmappedRevenue: number;
  supplierCount: number;
  nodeCount: number;
  categories: RevenueCategory[];
  companies: RevenueCompany[];
  insights: string[];
  regions: string[];
  categoryOptions: Array<{ id: string; label: string }>;
};

export type RevenueMapFilters = {
  tier: 'all' | 'critical' | 'high' | 'other';
  categoryId: string;
  region: string;
  sizeBy: 'revenue' | 'mentions';
  colorBy: 'growth' | 'evidence' | 'share';
};

export const UNMAPPED_COMPANY_ID = 'unmapped';
const OTHER_COMPANY_PREFIX = 'other:';
const MAX_CATEGORY_TILES = 12;
const MAX_COMPANIES_PER_CATEGORY = 5;
const MIN_CATEGORY_SHARE = 0.025;

type KnownBrand = {
  id: string;
  name: string;
  short: string;
  country?: string;
  test: RegExp;
};

const KNOWN_BRANDS: KnownBrand[] = [
  { id: 'st', name: 'STMicroelectronics', short: 'ST', country: 'France', test: /stmicro/i },
  { id: 'ti', name: 'Texas Instruments', short: 'TI', country: 'United States', test: /texas instruments/i },
  { id: 'nxp', name: 'NXP', short: 'NXP', country: 'Netherlands', test: /\bnxp\b/i },
  { id: 'infineon', name: 'Infineon', short: 'Infineon', country: 'Germany', test: /infineon/i },
  { id: 'qorvo', name: 'Qorvo', short: 'Qorvo', country: 'United States', test: /qorvo/i },
  { id: 'adi', name: 'Analog Devices', short: 'ADI', country: 'United States', test: /analog devices|\badi\b/i },
  { id: 'onsemi', name: 'onsemi', short: 'ON', country: 'United States', test: /onsemi|on semi/i },
  { id: 'qualcomm', name: 'Qualcomm', short: 'QCOM', country: 'United States', test: /qualcomm/i },
  { id: 'mediatek', name: 'MediaTek', short: 'MTK', country: 'Taiwan', test: /mediatek/i },
  { id: 'broadcom', name: 'Broadcom', short: 'AVGO', country: 'United States', test: /broadcom/i },
  { id: 'murata', name: 'Murata', short: 'Murata', country: 'Japan', test: /murata/i },
  { id: 'tdk', name: 'TDK', short: 'TDK', country: 'Japan', test: /\btdk\b/i },
  { id: 'molex', name: 'Molex', short: 'Molex', country: 'United States', test: /molex/i },
  { id: 'amphenol', name: 'Amphenol', short: 'Amphenol', country: 'United States', test: /amphenol/i },
  { id: 'te', name: 'TE Connectivity', short: 'TE', country: 'Switzerland', test: /te connectivity/i },
  { id: 'flex', name: 'Flex', short: 'Flex', country: 'Singapore', test: /\bflex\b/i },
  { id: 'jabil', name: 'Jabil', short: 'Jabil', country: 'United States', test: /jabil/i },
  { id: 'chicony', name: 'Chicony', short: 'Chicony', country: 'Taiwan', test: /chicony/i },
  { id: 'neweb', name: 'NeWeb', short: 'NeWeb', country: 'Taiwan', test: /neweb/i },
  { id: '3m', name: '3M', short: '3M', country: 'United States', test: /\b3m\b/i },
  { id: 'microchip', name: 'Microchip', short: 'MCHP', country: 'United States', test: /microchip/i },
  { id: 'delta', name: 'Delta', short: 'Delta', country: 'Taiwan', test: /\bdelta electronics|\bdelta\b/i },
  { id: 'foxconn', name: 'Foxconn', short: 'Foxconn', country: 'Taiwan', test: /foxconn/i },
];

type CategoryRule = {
  id: string;
  label: string;
  growthPct: number;
  test: (hay: string) => boolean;
};

const CATEGORY_RULES: CategoryRule[] = [
  {
    id: 'rf-semi',
    label: 'RF Semiconductors',
    growthPct: 18,
    test: (hay) =>
      /\b(stmicro|stmicroelectronics|\bst\b|qorvo|nxp|analog devices|\badi\b|sige|bicmos|beamformer|pulsar|fem|shiraz|catson|stsafe|rf (front|ic|chip|silicon)|phased-array silicon)\b/.test(
        hay,
      ),
  },
  {
    id: 'pcb',
    label: 'PCB & RF Laminate',
    growthPct: 22,
    test: (hay) => /\b(pcb|laminate|rogers|isola|\btuc\b|prepreg|rf board)\b/.test(hay),
  },
  {
    id: 'power-mgmt',
    label: 'Power Management',
    growthPct: 9,
    test: (hay) =>
      /\b(power management|pmic|dc.?dc|regulator|texas instruments|\bti\b|infineon|onsemi|richtek|voltage)\b/.test(
        hay,
      ),
  },
  {
    id: 'pcb-assy',
    label: 'Main PCB Assembly',
    growthPct: 14,
    test: (hay) => /\b(pcb assembly|contract manufact|ems|\bflex\b|jabil|pegatron)\b/.test(hay),
  },
  {
    id: 'connectors',
    label: 'Connectors & Cable',
    growthPct: 5,
    test: (hay) => /\b(connector|cable|molex|amphenol|te connectivity|\bte\b)\b/.test(hay),
  },
  {
    id: 'mechanical',
    label: 'Mechanical & Enclosure',
    growthPct: 3,
    test: (hay) =>
      /\b(enclosure|housing|kickstand|radome|mechanical|foxconn|laird|die.?cast|chassis)\b/.test(hay),
  },
  {
    id: 'antenna',
    label: 'Antenna & RF Components',
    growthPct: 7,
    test: (hay) => /\b(antenna|patch|murata|tdk|kyocera|filter|ceramic)\b/.test(hay),
  },
  {
    id: 'router',
    label: 'Router (In-Home)',
    growthPct: 8,
    test: (hay) => /\b(router|wi-?fi|wifi|mediatek|qualcomm|broadcom|ethernet)\b/.test(hay),
  },
  {
    id: 'psu',
    label: 'Power Supply',
    growthPct: 5,
    test: (hay) => /\b(power supply|psu|poe|adapter|lite-?on|\bdelta\b)\b/.test(hay),
  },
  {
    id: 'thermal',
    label: 'Thermal & Materials',
    growthPct: 2,
    test: (hay) =>
      /\b(thermal|adhesive|tim\b|gap pad|\b3m\b|henkel|material|chemical|dopant|copper|aluminum)\b/.test(
        hay,
      ),
  },
  {
    id: 'assy-test',
    label: 'Assembly & Test',
    growthPct: 4,
    test: (hay) => /\b(assembly|test|sanmina|celestica|odm)\b/.test(hay),
  },
];

const COMPANY_COUNTRY: Record<string, string> = {
  stmicroelectronics: 'France',
  qorvo: 'United States',
  nxp: 'Netherlands',
  'analog devices': 'United States',
  'texas instruments': 'United States',
  infineon: 'Germany',
  onsemi: 'United States',
  flex: 'Singapore',
  jabil: 'United States',
  foxconn: 'Taiwan',
  murata: 'Japan',
  tdk: 'Japan',
  mediatek: 'Taiwan',
  qualcomm: 'United States',
  broadcom: 'United States',
  molex: 'United States',
  'te connectivity': 'Switzerland',
  amphenol: 'United States',
  panasonic: 'Japan',
  rogers: 'United States',
};

export const DEFAULT_REVENUE_FILTERS: RevenueMapFilters = {
  tier: 'all',
  categoryId: 'all',
  region: 'all',
  sizeBy: 'revenue',
  colorBy: 'growth',
};

export function estimateTamUsd(index: TreeIndex): number {
  const companies = index.companyCount;
  const parts = index.partCount;
  if (parts >= 2000 || companies >= 80) return 8_200_000_000;
  if (parts >= 400 || companies >= 25) return 3_400_000_000;
  if (parts >= 80 || companies >= 8) return 1_100_000_000;
  return 420_000_000;
}

export function isOemSupplier(name: string, graph: ValueChainGraph): boolean {
  const hay = name.toLowerCase();
  if (isJunkSupplier(hay)) return true;
  if (/spacex|starlink/.test(hay)) return true;
  const root = graph.nodes.find((node) => node.id === graph.rootId);
  const owner = (root?.company || graph.ticker || '').toLowerCase();
  if (owner && hay.includes(owner) && owner.length > 3) return true;
  return false;
}

export function displayCompanyName(name: string): string {
  const brand = knownBrand(name);
  if (brand) return brand.name;
  const cleaned = name
    .replace(/\(.*?\)/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/,?\s*(co\.?,?\s*ltd\.?|inc\.?|llc|gmbh|s\.?a\.?|limited|corporation)\.?$/i, '')
    .trim();
  const words = cleaned.split(/\s+/).filter(Boolean);
  if (words.length > 3) return words.slice(0, 3).join(' ');
  return shortName(cleaned || name);
}

export function knownBrand(name: string): KnownBrand | null {
  return KNOWN_BRANDS.find((brand) => brand.test.test(name)) ?? null;
}

function isJunkSupplier(hay: string): boolean {
  if (knownBrand(hay)) return false;
  const value = hay.trim().toLowerCase();
  if (value.length < 4) return true;
  if (/\b(amazon|recommended|example heat|heat-frame|suppliers$)\b/.test(value)) return true;
  if (/[&/]+$/.test(value)) return true;
  if (/unknown|undisclosed|not disclosed|not public|n\/a|unidentified|cannot be|no public/.test(value)) {
    return true;
  }
  if (/^(inc|ltd|llc|gmbh|co|corp)\.?$/.test(value)) return true;
  if (/\bgeneric hardware\b|\bhardware supplier\b/.test(value)) return true;
  if (/^(but|and|or|the|a|an|for|with|other|where|using)\b/.test(value)) return true;
  if (/\bis not\b|\binferred from\b|\bno evidence\b|\bor other\b|secure-?ic/.test(value)) return true;
  if (
    /\b(golf|bicycle|bike|ski|pletscher|full swing|adafruit|marketing|where using|adapted)\b/.test(
      value,
    )
  ) {
    return true;
  }
  if (/\b(psu pcb|utp-|utr-|pcb assembler)\b/.test(value)) return true;
  if (value.split(/\s+/).length > 6) return true;
  return false;
}

function looksLikeCompany(name: string): boolean {
  if (knownBrand(name)) return true;
  if (isJunkSupplier(name)) return false;
  if (!/[A-Z]/.test(name)) return false;
  if (name.trim().length < 4) return false;
  if (/^[A-Z]{2,5}-?\d/.test(name)) return false;
  if (/,/.test(name) && /\b(where|using|adapted|design)\b/i.test(name)) return false;
  return true;
}

export function classifyCategory(
  node: ValueChainNode,
  ancestors: ValueChainNode[],
  companyName: string,
): { id: string; label: string; growthPct: number } {
  const hay = [
    companyName,
    node.name,
    node.industry,
    node.type,
    node.part_class,
    node.manufacturer,
    ...ancestors.map((item) => item.name),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  const match = CATEGORY_RULES.find((rule) => rule.test(hay));
  if (match) return { id: match.id, label: match.label, growthPct: match.growthPct };

  const assembly =
    [...ancestors].reverse().find((item) => item.type === 'Assembly / Module') ?? ancestors[1];
  if (assembly) {
    return {
      id: slugify(assembly.name),
      label: shortName(assembly.name),
      growthPct: 4,
    };
  }
  return { id: 'other', label: 'Other Components', growthPct: 0 };
}

export function collectRevenueSlices(graph: ValueChainGraph, index: TreeIndex): RevenueSlice[] {
  const tam = estimateTamUsd(index);
  const slices: RevenueSlice[] = [];
  allocate(graph.rootId, tam, graph, index, slices);
  return slices;
}

export function buildRevenueMap(
  graph: ValueChainGraph,
  index: TreeIndex,
  slices: RevenueSlice[],
  filters: RevenueMapFilters,
): RevenueMapModel {
  const filtered = slices.filter((slice) => matchesFilters(slice, filters));
  const unmappedRevenue = filtered
    .filter((slice) => slice.companyId === UNMAPPED_COMPANY_ID)
    .reduce((sum, slice) => sum + slice.revenue, 0);
  const mapped = filtered.filter((slice) => slice.companyId !== UNMAPPED_COMPANY_ID);
  const nodeById = index.byId;
  const grouped = new Map<string, RevenueSlice[]>();

  mapped.forEach((slice) => {
    const list = grouped.get(slice.categoryId) ?? [];
    list.push(slice);
    grouped.set(slice.categoryId, list);
  });

  const rawCategories: RevenueCategory[] = [...grouped.entries()]
    .map(([categoryId, categorySlices]) => {
      const label = categorySlices[0]?.categoryLabel ?? categoryId;
      const companies = rollupCompanies(categorySlices, nodeById, filters.sizeBy, MAX_COMPANIES_PER_CATEGORY);
      const revenue = companies.reduce((sum, company) => sum + company.revenue, 0);
      return { id: categoryId, label, revenue, companies };
    })
    .sort((a, b) => b.revenue - a.revenue);

  const categories = collapseSmallCategories(rawCategories, filters.sizeBy);
  const mappedRevenue = categories.reduce((sum, category) => sum + category.revenue, 0);
  if (unmappedRevenue > 0) {
    const various: RevenueCompany = {
      id: UNMAPPED_COMPANY_ID,
      name: 'Various',
      country: 'Unknown',
      revenue: unmappedRevenue,
      sizeValue: unmappedRevenue,
      mentions: 0,
      growthPct: null,
      evidence: 'unknown',
      partNames: [],
      node: null,
    };
    const existing = categories.find(
      (category) => category.id === 'other' || category.label === 'Other Components',
    );
    if (existing) {
      existing.revenue += unmappedRevenue;
      existing.companies.push(various);
    } else {
      categories.push({
        id: 'unmapped',
        label: 'Other Components',
        revenue: unmappedRevenue,
        companies: [various],
      });
    }
  }
  const companies = rollupCompanies(mapped, nodeById, 'revenue').filter(
    (company) => !isSyntheticCompany(company.id),
  );
  const totalRevenue = mappedRevenue + unmappedRevenue;
  const regions = unique(
    mapped
      .map((slice) => slice.country)
      .filter((country) => country && country !== 'Unknown'),
  ).sort();
  const categoryOptions = uniqueLabels(rawCategories).sort((a, b) => a.label.localeCompare(b.label));

  return {
    totalRevenue,
    mappedRevenue,
    unmappedRevenue,
    supplierCount: companies.length,
    nodeCount: graph.nodes.length,
    categories,
    companies,
    insights: deriveInsights(companies, categories, mappedRevenue, unmappedRevenue),
    regions,
    categoryOptions,
  };
}

export function formatUsdCompact(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(1)}B`;
  if (abs >= 100_000_000) return `$${(value / 1_000_000).toFixed(0)}M`;
  if (abs >= 1_000_000) return `$${(value / 1_000_000).toFixed(0)}M`;
  if (abs >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
  return `$${Math.round(value)}`;
}

export function formatGrowth(value: number | null): string {
  if (value == null || !Number.isFinite(value)) return '—';
  const rounded = Math.round(value);
  return `${rounded > 0 ? '+' : ''}${rounded}%`;
}

export function growthTone(value: number | null): {
  bg: string;
  fg: string;
  border: string;
} {
  if (value == null || !Number.isFinite(value)) {
    return { bg: 'bg-gray-200', fg: 'text-gray-600', border: 'border-gray-300' };
  }
  if (value > 20) return { bg: 'bg-emerald-800', fg: 'text-white', border: 'border-emerald-900' };
  if (value >= 10) return { bg: 'bg-emerald-400', fg: 'text-emerald-950', border: 'border-emerald-500' };
  if (value >= 0) return { bg: 'bg-emerald-100', fg: 'text-emerald-950', border: 'border-emerald-200' };
  if (value > -10) return { bg: 'bg-rose-200', fg: 'text-rose-950', border: 'border-rose-300' };
  return { bg: 'bg-rose-500', fg: 'text-white', border: 'border-rose-600' };
}

export function evidenceTone(evidence: RevenueEvidence): {
  bg: string;
  fg: string;
  border: string;
} {
  if (evidence === 'confirmed') {
    return { bg: 'bg-emerald-600', fg: 'text-white', border: 'border-emerald-700' };
  }
  if (evidence === 'inferred' || evidence === 'mixed') {
    return { bg: 'bg-amber-100', fg: 'text-amber-950', border: 'border-amber-200' };
  }
  return { bg: 'bg-gray-200', fg: 'text-gray-700', border: 'border-gray-300' };
}

export function isSyntheticCompany(id: string): boolean {
  return id === UNMAPPED_COMPANY_ID || id.startsWith(OTHER_COMPANY_PREFIX);
}

function allocate(
  nodeId: string,
  dollars: number,
  graph: ValueChainGraph,
  index: TreeIndex,
  slices: RevenueSlice[],
): void {
  if (dollars <= 0) return;
  const node = index.byId.get(nodeId);
  if (!node || node.type === 'Company') return;

  const suppliers = resolveSuppliers(node, graph, index);
  if (suppliers.length) {
    const each = dollars / suppliers.length;
    const rank = index.inboundBom.get(nodeId)?.rank ?? 6;
    const ancestors = ancestorsOf(index, nodeId);
    suppliers.forEach((supplier) => {
      const category = classifyCategory(node, ancestors, supplier.name);
      slices.push({
        companyId: supplier.id,
        companyName: supplier.name,
        categoryId: category.id,
        categoryLabel: category.label,
        country: supplier.country || countryFor(supplier.name) || 'Unknown',
        revenue: each,
        rank,
        evidence: node.evidence_status || supplier.evidence_status || 'unknown',
        partId: node.id,
        partName: node.name,
      });
    });
    return;
  }

  const kids = childrenOf(index, nodeId).filter((child) => child.type !== 'Company');
  if (!kids.length) {
    const ancestors = ancestorsOf(index, nodeId);
    const category = classifyCategory(node, ancestors, 'Various');
    slices.push({
      companyId: UNMAPPED_COMPANY_ID,
      companyName: 'Various',
      categoryId: category.id,
      categoryLabel: category.label,
      country: 'Unknown',
      revenue: dollars,
      rank: index.inboundBom.get(nodeId)?.rank ?? 6,
      evidence: node.evidence_status || 'unknown',
      partId: node.id,
      partName: node.name,
    });
    return;
  }

  const weights = kids.map((child) => 1 / Math.max(1, index.inboundBom.get(child.id)?.rank ?? 5));
  const weightSum = weights.reduce((sum, value) => sum + value, 0) || 1;
  kids.forEach((child, i) => {
    allocate(child.id, dollars * (weights[i] / weightSum), graph, index, slices);
  });
}

function resolveSuppliers(
  node: ValueChainNode,
  graph: ValueChainGraph,
  index: TreeIndex,
): ValueChainNode[] {
  const rawNames = [
    ...companiesOf(index, node.id).map((company) => company.name),
    node.company,
    node.manufacturer,
  ]
    .filter(Boolean)
    .flatMap((value) => splitSupplierNames(value as string));

  const seen = new Set<string>();
  const suppliers: ValueChainNode[] = [];
  rawNames.forEach((name) => {
    if (isOemSupplier(name, graph) || !looksLikeCompany(name)) return;
    const brand = knownBrand(name);
    const label = brand?.name ?? name;
    const key = slugify(label);
    if (seen.has(key)) return;
    seen.add(key);
    const match = matchCompany(index, label);
    suppliers.push({
      id: match?.id ?? (brand ? `brand:${brand.id}` : `named:${key}`),
      name: label,
      type: 'Company',
      description: match?.description ?? `Named manufacturer on ${node.name}`,
      country: brand?.country || match?.country || countryFor(label),
    });
  });
  return suppliers.slice(0, 4);
}

function splitSupplierNames(raw: string): string[] {
  const brands = KNOWN_BRANDS.filter((brand) => brand.test.test(raw)).map((brand) => brand.name);
  if (brands.length > 1) return unique(brands);
  if (brands.length === 1 && !/[;,]|\bor\b|\band\b/.test(raw)) return brands;

  return unique(
    raw
      .split(/\s+or\s+|\s+and\s+|[;,/]/i)
      .map((part) => cleanSupplierLabel(part))
      .filter((part) => part.length > 2 && looksLikeCompany(part)),
  );
}

function matchCompany(index: TreeIndex, name: string): ValueChainNode | null {
  const needle = name.toLowerCase();
  if (needle.length < 3) return null;
  let best: ValueChainNode | null = null;
  for (const node of index.byId.values()) {
    if (node.type !== 'Company') continue;
    const hay = node.name.toLowerCase();
    if (hay === needle) return node;
    if (needle.length >= 5 && (hay.includes(needle) || needle.includes(hay))) {
      best = node;
    }
  }
  return best;
}

function matchesFilters(slice: RevenueSlice, filters: RevenueMapFilters): boolean {
  if (filters.categoryId !== 'all' && slice.categoryId !== filters.categoryId) return false;
  if (filters.region !== 'all' && slice.country !== filters.region) return false;
  if (filters.tier === 'critical' && slice.rank > 2) return false;
  if (filters.tier === 'high' && (slice.rank < 3 || slice.rank > 4)) return false;
  if (filters.tier === 'other' && slice.rank < 5) return false;
  return true;
}

function rollupCompanies(
  slices: RevenueSlice[],
  nodeById: Map<string, ValueChainNode>,
  sizeBy: RevenueMapFilters['sizeBy'],
  limit = Number.POSITIVE_INFINITY,
): RevenueCompany[] {
  const byId = new Map<string, RevenueSlice[]>();
  slices.forEach((slice) => {
    const list = byId.get(slice.companyId) ?? [];
    list.push(slice);
    byId.set(slice.companyId, list);
  });

  const companies = [...byId.entries()].map(([id, companySlices]) => {
    const revenue = companySlices.reduce((sum, slice) => sum + slice.revenue, 0);
    const mentions = unique(companySlices.map((slice) => slice.partId)).length;
    const partNames = unique(companySlices.map((slice) => shortName(slice.partName)));
    const evidence = blendEvidence(companySlices.map((slice) => slice.evidence));
    const growth = modeledGrowth(companySlices, evidence);
    const node = nodeById.get(id) ?? null;
    return {
      id,
      name: companySlices[0]?.companyName ?? id,
      country: companySlices[0]?.country ?? 'Unknown',
      revenue,
      sizeValue: sizeBy === 'mentions' ? mentions : revenue,
      mentions,
      growthPct: id === UNMAPPED_COMPANY_ID ? null : growth,
      evidence,
      partNames: partNames.slice(0, 6),
      node,
    } satisfies RevenueCompany;
  });

  companies.sort((a, b) => b.sizeValue - a.sizeValue);
  if (!Number.isFinite(limit) || companies.length <= limit) return companies;

  const total = companies.reduce((sum, company) => sum + company.sizeValue, 0) || 1;
  const named: RevenueCompany[] = [];
  const rest: RevenueCompany[] = [];
  companies.forEach((company) => {
    if (
      named.length < limit - 1 &&
      company.sizeValue / total >= 0.05 &&
      !isSyntheticCompany(company.id)
    ) {
      named.push(company);
      return;
    }
    rest.push(company);
  });
  if (!rest.length) return named;
  named.push({
    id: `${OTHER_COMPANY_PREFIX}${rest[0]?.id ?? 'rest'}`,
    name: 'Other',
    country: 'Unknown',
    revenue: rest.reduce((sum, company) => sum + company.revenue, 0),
    sizeValue: rest.reduce((sum, company) => sum + company.sizeValue, 0),
    mentions: rest.reduce((sum, company) => sum + company.mentions, 0),
    growthPct: null,
    evidence: 'unknown',
    partNames: [],
    node: null,
  });
  return named;
}

function collapseSmallCategories(
  categories: RevenueCategory[],
  sizeBy: RevenueMapFilters['sizeBy'],
): RevenueCategory[] {
  if (categories.length <= MAX_CATEGORY_TILES) return categories;
  const total = categories.reduce((sum, category) => sum + category.revenue, 0) || 1;
  const kept: RevenueCategory[] = [];
  const rest: RevenueCategory[] = [];
  categories.forEach((category, index) => {
    if (index < MAX_CATEGORY_TILES - 1 && category.revenue / total >= MIN_CATEGORY_SHARE) kept.push(category);
    else rest.push(category);
  });
  if (!rest.length) return kept;
  const mergedSlices = rest.flatMap((category) =>
    category.companies.map((company) => ({
      companyId: company.id,
      companyName: company.name,
      categoryId: 'other',
      categoryLabel: 'Other Components',
      country: company.country,
      revenue: company.revenue,
      rank: 6,
      evidence: company.evidence,
      partId: company.id,
      partName: company.partNames[0] ?? company.name,
    })),
  );
  const companies = rollupCompanies(mergedSlices, new Map(), sizeBy, MAX_COMPANIES_PER_CATEGORY);
  kept.push({
    id: 'other',
    label: 'Other Components',
    revenue: companies.reduce((sum, company) => sum + company.revenue, 0),
    companies,
  });
  return kept.sort((a, b) => b.revenue - a.revenue);
}

function modeledGrowth(slices: RevenueSlice[], evidence: RevenueEvidence): number {
  const first = slices[0];
  const rule = CATEGORY_RULES.find((item) => item.id === first?.categoryId);
  const base = rule?.growthPct ?? 4;
  const confirmedShare =
    slices.filter((slice) => slice.evidence === 'confirmed').length / Math.max(1, slices.length);
  const evidenceBoost =
    evidence === 'confirmed' ? 6 : evidence === 'inferred' ? 1 : evidence === 'mixed' ? 2 : -2;
  return base + (confirmedShare - 0.45) * 10 + evidenceBoost;
}

function blendEvidence(values: string[]): RevenueEvidence {
  const set = new Set(values.map((value) => value || 'unknown'));
  if (set.size > 1) return 'mixed';
  if (set.has('confirmed')) return 'confirmed';
  if (set.has('inferred')) return 'inferred';
  return 'unknown';
}

function deriveInsights(
  companies: RevenueCompany[],
  categories: RevenueCategory[],
  mappedRevenue: number,
  unmappedRevenue: number,
): string[] {
  const insights: string[] = [];
  const top = companies[0];
  if (top && mappedRevenue > 0) {
    insights.push(
      `${displayCompanyName(top.name)} is the largest single supplier, estimated at ${formatUsdCompact(top.revenue)} (${Math.round((top.revenue / mappedRevenue) * 100)}% of mapped spend).`,
    );
  }
  const notable = categories.find((category) => category.id === 'pcb' || /laminate|pcb/i.test(category.label));
  if (notable) {
    insights.push(
      `${notable.label} shows meaningful exposure as terminal volumes scale.`,
    );
  } else if (categories[0]) {
    insights.push(`${categories[0].label} is the largest cost bucket.`);
  }
  const top5 = companies.slice(0, 5).reduce((sum, company) => sum + company.revenue, 0);
  if (companies.length && mappedRevenue > 0) {
    insights.push(
      `Supply chain is diversified across ${companies.length} named suppliers; top 5 represent ~${Math.round((top5 / mappedRevenue) * 100)}% of mapped spend.`,
    );
  }
  const total = mappedRevenue + unmappedRevenue;
  if (unmappedRevenue > 0 && total > 0) {
    insights.push(
      `${Math.round((unmappedRevenue / total) * 100)}% of estimated spend is still unmapped to a named supplier.`,
    );
  } else {
    insights.push('Component costs in boards and metals can move independently of semiconductor prices.');
  }
  return insights.slice(0, 4);
}

function countryFor(name: string): string | undefined {
  return knownBrand(name)?.country ?? COMPANY_COUNTRY[name.toLowerCase()];
}

function cleanSupplierLabel(raw: string): string {
  return raw
    .replace(/^(likely|probably|best-supported:?)\s+/i, '')
    .replace(/\(.*?\)/g, ' ')
    .split(/\s+for\s+/i)[0]
    .replace(/\s*[&/]+\s*$/g, '')
    .split(/[;,]/)[0]
    .replace(/\s+/g, ' ')
    .trim();
}

function slugify(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'other';
}

function unique(values: string[]): string[] {
  return [...new Set(values.filter(Boolean))];
}

function uniqueLabels(categories: RevenueCategory[]): Array<{ id: string; label: string }> {
  const seen = new Map<string, string>();
  categories.forEach((category) => {
    if (!seen.has(category.id)) seen.set(category.id, category.label);
  });
  return [...seen.entries()].map(([id, label]) => ({ id, label }));
}
